// filepath: apps/web/src/features/lists/gantt/GanttCountBanner.tsx
//
// FR-UI-VIEW-3 AC-2 — cap banner when the server returns a partial
// result set.
//
// Mirrors the KanbanCountBanner shape (locked copy + predicate +
// `null` when off) but uses the design-system Banner primitive
// instead of a hand-rolled notice so the gantt module ties into
// the FR-UI-VIEW-3 manifest's "warning" tone requirement (per
// docs/design/FR-UI-VIEW-3/composition/gantt-view/manifest.yaml
// `design_tokens_used.colors` — color-warning is wired here).
//
// Triggered when:
//   - records.length >= cap  (server reached the cap)
//   - totalCount is non-null AND totalCount > cap (server indicates
//     a partial result).
//
// The banner surfaces the rendered count as
// `{shown} of {total} records shown.` and offers a load-more
// affordance via the passed-in `onLoadMore` callback.
//
// INVARIANT REMINDERS:
//   - The banner is off by default — it surfaces only when the
//     server indicates a partial result.
//   - The chrome line is static when total is missing; i18n is
//     wired for the {count} placeholder version (lists.gantt.capBanner)
//     but the legacy "hard-coded" path remains for backward-compat
//     with the FR-UI-VIEW-1 banner.
//   - INV-9 — every value is rendered as a React text node; React
//     escapes by default. The user-supplied `onLoadMore` is the only
//     "action" — a Button primitive is composed.

import React from "react";
import { Banner } from "@/design-system/Banner";
import { Button } from "@/design-system/Button";
import { useTranslation } from "@/i18n/useTranslation";

export interface GanttCountBannerProps {
  /** Number of records currently rendered as bars. */
  shown: number;
  /**
   * Server-supplied total record count across the entity type. Null
   * when the server wire omits the additive `total_count` field
   * (older deployments) — the banner stays off in that case.
   */
  total: number | null;
  /**
   * Cap above which the banner is considered active. Defaults to
   * `GANTT_RECORDS_CAP` (500). Exposed as a prop so a future FR
   * bumping the cap can pass a different threshold.
   */
  cap?: number;
  /**
   * Fired when the user clicks the load-more action. When provided,
   * a Button is rendered below the banner copy. The parent is
   * responsible for triggering the next-page fetch + the cache-key
   * invalidation pattern; the banner only surfaces the affordance.
   */
  onLoadMore?: () => void;
  /** Test hook forwarded onto the rendered root. */
  "data-testid"?: string;
}

/**
 * Predicate — should the banner be visible? Per FR-UI-VIEW-3 AC-2,
 * the banner triggers when the server caps the result set, i.e.
 * `shown >= cap` AND `total > cap`.
 *
 * `total < shown` is impossible under the wire contract (server
 * always supplies total ≥ page size) but we treat it as `false` to
 * remain defensive against a misbehaving backend (banner stays off
 * when the data is internally inconsistent).
 */
export function shouldShowGanttCountBanner(
  shown: number,
  total: number | null,
  cap: number,
): boolean {
  if (total === null) return false;
  if (!Number.isFinite(total) || total <= 0) return false;
  if (!Number.isFinite(shown) || shown < 0) return false;
  if (total <= cap) return false;
  if (shown < cap) return false;
  return shown < total;
}

/**
 * GanttCountBanner — locked-copy partial-result notice.
 *
 * Renders only when `shouldShowGanttCountBanner(shown, total, cap)`
 * returns true. Otherwise returns `null` (the parent does not
 * need to track visibility).
 *
 * The Banner primitive is configured with `tone="warning"` per
 * the FR-UI-VIEW-3 manifest's color policy.
 */
export function GanttCountBanner({
  shown,
  total,
  cap = 500,
  onLoadMore,
  "data-testid": testId = "gantt-count-banner",
}: GanttCountBannerProps): React.ReactElement | null {
  const { t } = useTranslation();
  if (!shouldShowGanttCountBanner(shown, total, cap)) {
    return null;
  }
  // {count} carries the rendered count so i18n can substitute
  // pluralised forms without the renderer doing locale arithmetic.
  // {total} carries the server-supplied total. The fallback in the
  // source below is the locked English copy (mirrors
  // KanbanCountBanner's LOCKED COPY contract per FR-UI-VIEW-1 AC-3).
  const copy = t(
    "lists.gantt.capBanner",
    `${shown} of ${total} records shown.`,
    { count: shown, total: total ?? 0 },
  );
  return (
    <div
      data-testid={testId}
      data-gantt-shown={shown}
      data-gantt-total={total}
      data-gantt-cap={cap}
      className="gantt-count-banner"
      style={{
        margin: "var(--space-2) 0",
        display: "flex",
        flexDirection: "column",
        gap: "var(--space-2)",
      }}
    >
      <Banner tone="warning" data-testid={`${testId}-banner`}>
        {copy}
      </Banner>
      {onLoadMore ? (
        <div
          data-testid={`${testId}-actions`}
          style={{
            display: "flex",
            justifyContent: "flex-end",
          }}
        >
          <Button
            type="button"
            variant="secondary"
            size="sm"
            onClick={onLoadMore}
            data-testid={`${testId}-load-more`}
          >
            {t("lists.gantt.loadMore", "Load more")}
          </Button>
        </div>
      ) : null}
    </div>
  );
}
